import type { TaxonomyOption } from "cmw-client";

export type TreeOption = {
  value: string;
  label: string;
  children?: TreeOption[];
};

/** The API's taxonomy tree as a `TreeMultiCombobox` takes it: parents stay choices, not headings. */
export const toTreeOptions = (options: TaxonomyOption[] = []): TreeOption[] =>
  options.map((option) => ({
    value: option.slug,
    label: option.label,
    ...(option.children?.length ? { children: toTreeOptions(option.children) } : {}),
  }));

/* Two levels deep — a parent and its children — which is all the taxonomy tree carries. */

const parentOf = (tree: TreeOption[], value: string) =>
  tree.find((option) => option.children?.some((child) => child.value === value));

const hasSelectedChild = (option: TreeOption | undefined, selected: string[]) =>
  !!option?.children?.some((child) => selected.includes(child.value));

/** A parent can't be unticked while any of its children is ticked. */
export const isLockedParent = (tree: TreeOption[], selected: string[], value: string) =>
  hasSelectedChild(
    tree.find((option) => option.value === value),
    selected,
  );

/**
 * `selected` with `value` ticked or unticked. A ticked child ticks its parent, and the parent goes
 * when its last ticked child does; a parent can be ticked on its own, and a locked one stays.
 */
export const toggleTreeValue = (tree: TreeOption[], selected: string[], value: string): string[] => {
  const parent = parentOf(tree, value);

  if (!selected.includes(value)) {
    return parent && !selected.includes(parent.value) ? [...selected, parent.value, value] : [...selected, value];
  }

  if (isLockedParent(tree, selected, value)) {
    return selected;
  }

  const next = selected.filter((v) => v !== value);
  return parent && !hasSelectedChild(parent, next) ? next.filter((v) => v !== parent.value) : next;
};

/** `selected` plus the parent of each ticked child. */
export const withImpliedParents = (tree: TreeOption[], selected: string[]) => [
  ...new Set([...selected, ...selected.flatMap((value) => parentOf(tree, value)?.value ?? [])]),
];

/** `selected` minus each parent a ticked child already implies: "Asia, Lebanon" reads as "Lebanon". */
export const withoutImpliedParents = (tree: TreeOption[], selected: string[]) =>
  selected.filter((value) => !isLockedParent(tree, selected, value));
